<template>
  <header class="site-header sticky top-0 z-[1002] bg-white shadow-[0_1px_0_#e9ecef]">

    <!-- ① Optionele banner-afbeelding -->
    <div v-if="bannerImg" class="w-full overflow-hidden">
      <img :src="bannerImg" :alt="bannerAlt" class="w-full h-auto object-cover max-h-[80px]">
    </div>
    <!-- Lege slot voor banner (verwijder de v-if hierboven en zet bannerImg om deze te activeren) -->

    <!-- ② Topbar -->
    <div class="bg-[#111] text-white text-[0.8rem] py-[0.45rem]">
      <div class="max-w-screen-lg mx-auto px-4 flex items-center justify-between gap-4">
        <div class="flex items-center gap-4 flex-wrap">
          <span class="flex items-center gap-1.5 text-white/80">
            <i class="bi bi-truck text-white/60"></i>
            Gratis verzending v.a. €50
          </span>
          <span class="text-white/25 hidden sm:inline">|</span>
          <span class="flex items-center gap-1.5 text-white/80 hidden sm:flex">
            <i class="bi bi-credit-card text-white/60"></i>
            Achteraf betalen beschikbaar
          </span>
        </div>
        <router-link to="/klantenservice" class="flex items-center gap-1.5 no-underline shrink-0">
          <span class="flex text-white/70 text-[0.7rem]" aria-hidden="true">
            <i class="bi bi-star-fill"></i><i class="bi bi-star-fill"></i><i class="bi bi-star-fill"></i><i class="bi bi-star-fill"></i><i class="bi bi-star-half"></i>
          </span>
          <span class="text-white/80 font-semibold">4,9 uit 380+ reviews</span>
        </router-link>
      </div>
    </div>

    <!-- ② Main header -->
    <div class="bg-white py-3 border-b border-[#e9ecef]">
      <div class="max-w-screen-lg mx-auto px-4">
        <div class="flex items-center gap-4">

          <!-- Hamburger (mobiel) -->
          <button
            type="button"
            :aria-label="showMobileMenu ? 'Menu sluiten' : 'Menu openen'"
            class="md:hidden bg-transparent border-none text-[1.375rem] text-[#333] cursor-pointer p-1 -ml-1"
            @click="showMobileMenu = !showMobileMenu"
          >
            <i class="bi" :class="showMobileMenu ? 'bi-x' : 'bi-list'"></i>
          </button>

          <!-- Logo -->
          <router-link to="/" class="shrink-0 flex items-center no-underline" aria-label="Plug2Charge.nl - home">
            <Logo :height="40" />
          </router-link>

          <!-- Zoekbalk -->
          <div class="flex-1 max-w-[600px] mx-auto hidden md:flex border-[1.5px] border-[#e9ecef] rounded-[6px] bg-[#fafafa] focus-within:border-[#FFD500] focus-within:bg-white focus-within:shadow-[0_0_0_3px_rgba(255,213,0,0.20)] relative">
            <input
              ref="searchInputRef"
              v-model="searchQuery"
              @keyup.enter="handleSearch"
              @input="onSearchInput"
              @focus="onSearchFocus"
              @blur="onSearchBlur"
              @keydown.down.prevent="moveSuggestion(1)"
              @keydown.up.prevent="moveSuggestion(-1)"
              @keydown.escape="closeSuggestions"
              type="text"
              autocomplete="off"
              class="flex-1 px-4 py-2.5 border-none outline-none bg-transparent text-[0.875rem] placeholder:text-[#aaa]"
              placeholder="Waar ben je naar op zoek?"
            >
            <!-- Zoeksuggesties -->
            <transition name="fade-slide">
              <div
                v-if="showSuggestions && (suggestions.length > 0 || suggestionsLoading)"
                class="absolute top-[calc(100%+6px)] left-0 right-0 bg-white border border-[#e9ecef] rounded-[10px] shadow-[0_8px_32px_rgba(0,0,0,0.12)] z-[300] overflow-hidden"
              >
                <div v-if="suggestionsLoading && suggestions.length === 0" class="px-4 py-3 text-[0.8rem] text-[#888]">
                  <i class="bi bi-arrow-repeat spin text-[#f0b800]"></i> Zoeken…
                </div>
                <template v-else>
                  <router-link
                    v-for="(item, i) in suggestions"
                    :key="item.id"
                    :to="`/product/${item.slug}`"
                    @click="closeSuggestions"
                    class="flex items-center gap-3 px-4 py-2.5 no-underline transition-colors border-b border-[#f5f5f5] last:border-b-0"
                    :class="{ 'bg-[#fffbe6]': i === activeSuggestion }"
                    style="color: #111"
                  >
                    <div class="w-10 h-10 shrink-0 rounded-[5px] bg-[#f8f9fa] border border-[#f0f0f0] flex items-center justify-center">
                      <img v-if="item.image" :src="item.image" :alt="item.name" class="w-full h-full object-contain">
                      <i v-else class="bi bi-image text-[#ccc] text-sm"></i>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="text-[0.85rem] font-medium truncate">{{ item.name }}</div>
                      <div class="text-[0.75rem] text-[#888]">{{ item.category }}</div>
                    </div>
                    <div class="shrink-0 text-right">
                      <div class="text-[0.875rem] font-bold">{{ formatPrice(item.price) }}</div>
                    </div>
                  </router-link>
                  <button @click="handleSearch" class="w-full px-4 py-2.5 bg-[#fffbe6] text-[0.82rem] font-semibold text-[#333] text-left hover:bg-[#fff3b0] flex items-center gap-2 border-none cursor-pointer">
                    <i class="bi bi-search text-[#b8860b]"></i>
                    Alle resultaten voor "{{ searchQuery }}"
                  </button>
                </template>
              </div>
            </transition>
            <button
              type="button"
              aria-label="Zoeken"
              @click="handleSearch"
              class="bg-[#FFD500] border-none px-5 text-[#111] cursor-pointer text-base hover:bg-[#f0b800] transition-colors rounded-r-[4px]"
            >
              <i class="bi bi-search"></i>
            </button>
          </div>

          <!-- Utility links (rechts) -->
          <div class="flex items-center gap-1 ml-auto md:ml-0">
            <router-link to="/account" class="hidden lg:flex items-center gap-1.5 px-3 py-2 text-[#555] no-underline text-[0.8125rem] hover:text-[#111] transition-colors rounded-[6px] hover:bg-[#f5f5f5]">
              <i class="bi bi-person"></i>
              <span>Mijn account</span>
            </router-link>
            <router-link to="/klantenservice" class="hidden lg:flex items-center gap-1.5 px-3 py-2 text-[#555] no-underline text-[0.8125rem] hover:text-[#111] transition-colors rounded-[6px] hover:bg-[#f5f5f5]">
              <i class="bi bi-headset"></i>
              <span>Klantenservice</span>
            </router-link>
            <router-link to="/cart" aria-label="Winkelwagen" class="relative w-10 h-10 flex items-center justify-center bg-[#FFD500] text-[#111] no-underline rounded-[6px] text-lg hover:bg-[#f0b800] transition-colors ml-1">
              <i class="bi bi-cart"></i>
              <span v-if="cartItemCount > 0" class="absolute top-px right-px bg-red-500 text-white rounded-full w-[18px] h-[18px] flex items-center justify-center text-[0.625rem] font-bold border-2 border-white">{{ cartItemCount }}</span>
            </router-link>
          </div>

        </div>
      </div>
    </div>

    <!-- ③ Nav bar -->
    <div class="nav-bar hidden md:block bg-white border-b border-[#e9ecef]" @mouseleave="closeAllMenus">
      <div class="max-w-screen-lg mx-auto px-4">
        <div class="flex items-center justify-between">
          <nav class="flex items-stretch gap-0">

            <router-link to="/categorie/laadpalen" class="nav-link px-4 h-11 text-[#444] no-underline font-medium text-[0.875rem] whitespace-nowrap inline-flex items-center">
              Laadpalen
            </router-link>

            <router-link to="/categorie/laadkabels" class="nav-link px-4 h-11 text-[#444] no-underline font-medium text-[0.875rem] whitespace-nowrap inline-flex items-center">
              Laadkabels
            </router-link>

            <!-- Thuisbatterijen dropdown -->
            <div class="flex items-stretch relative" @mouseenter="openMenu('batterijen')">
              <div
                class="nav-link px-4 h-11 text-[#444] font-medium text-[0.875rem] whitespace-nowrap inline-flex items-center gap-1.5 cursor-pointer"
                :class="{ active: activeMenu === 'batterijen' }"
                @click="toggleMenu('batterijen')"
              >
                Thuisbatterijen
                <i class="bi bi-chevron-down text-[0.6rem] text-[#888] transition-transform duration-200" :class="{ 'rotate-180': activeMenu === 'batterijen' }"></i>
              </div>
              <transition name="menu-drop">
                <div v-if="activeMenu === 'batterijen'" class="simple-dropdown">
                  <router-link to="/categorie/thuisbatterijen" class="dropdown-item" @click="closeAllMenus">
                    <i class="bi bi-battery-full text-[#FFD500]"></i> Thuisbatterijen
                  </router-link>
                  <router-link to="/categorie/plug-in-batterijen" class="dropdown-item" @click="closeAllMenus">
                    <i class="bi bi-plug text-[#FFD500]"></i> Plug-in batterijen
                  </router-link>
                  <router-link to="/categorie/all-in-one" class="dropdown-item" @click="closeAllMenus">
                    <i class="bi bi-box-seam text-[#FFD500]"></i> All-in-one systemen
                  </router-link>
                  <router-link to="/categorie/complete-pakketten" class="dropdown-item" @click="closeAllMenus">
                    <i class="bi bi-grid text-[#FFD500]"></i> Complete pakketten
                  </router-link>
                </div>
              </transition>
            </div>

            <router-link to="/categorie/zonnepanelen" class="nav-link px-4 h-11 text-[#444] no-underline font-medium text-[0.875rem] whitespace-nowrap inline-flex items-center">
              Zonnepanelen
            </router-link>

            <router-link to="/categorie/omvormers" class="nav-link px-4 h-11 text-[#444] no-underline font-medium text-[0.875rem] whitespace-nowrap inline-flex items-center">
              Omvormers
            </router-link>

            <router-link to="/merken" class="nav-link px-4 h-11 text-[#444] no-underline font-medium text-[0.875rem] whitespace-nowrap inline-flex items-center">
              Merken
            </router-link>

            <router-link to="/acties" class="nav-link px-4 h-11 text-[#c0392b] no-underline font-semibold text-[0.875rem] whitespace-nowrap inline-flex items-center gap-1.5">
              <i class="bi bi-tag-fill text-[0.8rem]"></i> Aanbiedingen
            </router-link>

          </nav>

          <!-- Keuzehulp CTA -->
          <router-link
            to="/keuzehulp"
            class="inline-flex items-center gap-2 px-4 py-2 rounded-[6px] no-underline font-semibold text-[0.8125rem] bg-[#FFD500] text-[#111] hover:bg-[#f0b800] transition-colors shrink-0 whitespace-nowrap"
          >
            <i class="bi bi-ui-checks-grid"></i>
            Keuzehulp
          </router-link>
        </div>
      </div>
    </div>

    <!-- ④ Mobiel menu -->
    <transition name="slide-down">
      <div v-if="showMobileMenu" class="md:hidden fixed inset-0 top-[108px] bg-white z-[1001] overflow-y-auto">
        <div class="p-4 flex flex-col gap-1">
          <!-- Zoekbalk mobiel -->
          <div class="flex gap-2 mb-3">
            <input v-model="searchQuery" @keyup.enter="handleSearch" type="text" placeholder="Zoek producten..."
              class="flex-1 px-3 py-2.5 border-[1.5px] border-[#e9ecef] rounded-[8px] outline-none text-[0.875rem] focus:border-[#FFD500]">
            <button @click="handleSearch" class="bg-[#FFD500] hover:bg-[#f0b800] border-none px-4 rounded-[8px] text-[#111] cursor-pointer text-base">
              <i class="bi bi-search"></i>
            </button>
          </div>

          <router-link to="/categorie/laadpalen"       class="mob-item" @click="showMobileMenu=false"><i class="bi bi-plug"></i> Laadpalen</router-link>
          <router-link to="/categorie/laadkabels"      class="mob-item" @click="showMobileMenu=false"><i class="bi bi-lightning"></i> Laadkabels</router-link>
          <router-link to="/categorie/thuisbatterijen" class="mob-item" @click="showMobileMenu=false"><i class="bi bi-battery-full"></i> Thuisbatterijen</router-link>
          <router-link to="/categorie/zonnepanelen"    class="mob-item" @click="showMobileMenu=false"><i class="bi bi-sun"></i> Zonnepanelen</router-link>
          <router-link to="/categorie/omvormers"       class="mob-item" @click="showMobileMenu=false"><i class="bi bi-gear"></i> Omvormers</router-link>
          <router-link to="/merken"                    class="mob-item" @click="showMobileMenu=false"><i class="bi bi-award"></i> Merken</router-link>
          <router-link to="/acties"                    class="mob-item !text-[#c0392b] !border-[#c0392b]/30 !bg-[#fdf2f1]" @click="showMobileMenu=false"><i class="bi bi-tag-fill"></i> Aanbiedingen</router-link>
          <router-link to="/keuzehulp"                 class="mob-item !bg-[#FFD500] !border-[#FFD500] !text-[#111] !font-semibold" @click="showMobileMenu=false"><i class="bi bi-ui-checks-grid"></i> Keuzehulp</router-link>
          <router-link to="/account"                   class="mob-item mt-2" @click="showMobileMenu=false"><i class="bi bi-person"></i> Mijn account</router-link>
          <router-link to="/klantenservice"            class="mob-item" @click="showMobileMenu=false"><i class="bi bi-headset"></i> Klantenservice</router-link>
        </div>
      </div>
    </transition>

    <!-- Overlay -->
    <transition name="fade">
      <div v-if="showMobileMenu" class="fixed inset-0 top-[108px] bg-black/35 z-[1000] md:hidden" @click="showMobileMenu=false"></div>
    </transition>

  </header>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useCartStore } from '@stores/cart'
import { useWishlistStore } from '@stores/wishlist'
import Logo from '@components/Logo.vue'
import { useAnalytics } from '@composables/useAnalytics'
import { formatPrice } from '@/utils/price'

const router    = useRouter()
const cartStore = useCartStore()
const wishlistStore = useWishlistStore()
const { trackSearch } = useAnalytics()

const searchQuery    = ref('')
const showMobileMenu = ref(false)
const activeMenu     = ref(null)
const searchInputRef = ref(null)

// Banner-afbeelding boven de header (optioneel)
// Zet hier een URL om een promobanner te tonen, bijv.:
// const bannerImg = '/wp-content/uploads/2026/04/banner-zomer.jpg'
const bannerImg  = null
const bannerAlt  = 'Promotie banner'

// Zoeksuggesties
const suggestions        = ref([])
const suggestionsLoading = ref(false)
const showSuggestions    = ref(false)
const activeSuggestion   = ref(-1)
let   suggestDebounce    = null

const fetchSuggestions = async (q) => {
  if (q.length < 2) { suggestions.value = []; suggestionsLoading.value = false; return }
  suggestionsLoading.value = true
  try {
    const res = await fetch(`/_search?q=${encodeURIComponent(q)}`)
    if (res.ok) suggestions.value = await res.json()
  } catch { suggestions.value = [] }
  finally { suggestionsLoading.value = false }
}

const onSearchInput = () => {
  activeSuggestion.value = -1
  const q = searchQuery.value.trim()
  showSuggestions.value = q.length >= 2
  clearTimeout(suggestDebounce)
  if (q.length >= 2) {
    suggestionsLoading.value = true
    suggestDebounce = setTimeout(() => fetchSuggestions(q), 220)
  } else {
    suggestions.value = []
    suggestionsLoading.value = false
  }
}

const onSearchFocus = () => { if (searchQuery.value.trim().length >= 2) showSuggestions.value = true }
const onSearchBlur  = () => { setTimeout(() => { showSuggestions.value = false; activeSuggestion.value = -1 }, 200) }
const closeSuggestions = () => { showSuggestions.value = false; activeSuggestion.value = -1; suggestions.value = [] }
const moveSuggestion = (dir) => {
  if (!showSuggestions.value || !suggestions.value.length) return
  activeSuggestion.value = Math.max(-1, Math.min(suggestions.value.length - 1, activeSuggestion.value + dir))
}

const cartItemCount = computed(() => cartStore.itemCount)

const handleSearch = () => {
  if (searchQuery.value.trim()) {
    trackSearch(searchQuery.value.trim())
    router.push({ path: '/producten', query: { search: searchQuery.value.trim() } })
    searchQuery.value = ''
    closeSuggestions()
    showMobileMenu.value = false
  }
}

const openMenu     = (name) => { activeMenu.value = name }
const toggleMenu   = (name) => { activeMenu.value = activeMenu.value === name ? null : name }
const closeAllMenus = () => { activeMenu.value = null }

const lockScroll = () => {
  if (typeof window === 'undefined') return
  const y = window.scrollY
  document.body.style.position = 'fixed'
  document.body.style.top = `-${y}px`
  document.body.style.width = '100%'
}
const unlockScroll = () => {
  if (typeof window === 'undefined') return
  const y = document.body.style.top
  document.body.style.position = ''
  document.body.style.top = ''
  document.body.style.width = ''
  window.scrollTo(0, parseInt(y || '0') * -1)
}

import { watch } from 'vue'
watch(showMobileMenu, (v) => { v ? lockScroll() : unlockScroll() })

onMounted(() => { wishlistStore.initWishlist() })
onUnmounted(() => { if (typeof window !== 'undefined') unlockScroll() })
</script>

<style scoped>
.nav-link {
  border-bottom: 3px solid transparent;
  transition: color 0.18s, border-color 0.18s, background 0.18s;
  box-sizing: border-box;
}
.nav-link:hover,
.nav-link.router-link-active,
.nav-link.router-link-exact-active,
.nav-link.active {
  color: #111;
  border-bottom-color: #FFD500;
}

.nav-bar { position: relative; }

/* Simple dropdown */
.simple-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  background: #fff;
  border: 1.5px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.10);
  min-width: 220px;
  padding: 6px;
  z-index: 200;
}
.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem 0.875rem;
  border-radius: 6px;
  text-decoration: none;
  color: #333;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background 0.15s;
}
.dropdown-item:hover { background: #fffbe6; }

/* Mobile nav items */
.mob-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0.875rem;
  background: #fafafa;
  border: 1.5px solid transparent;
  border-radius: 10px;
  text-decoration: none;
  color: #333;
  font-size: 0.9375rem;
  font-weight: 500;
  transition: background 0.15s, border-color 0.15s;
}
.mob-item:hover,
.mob-item.router-link-active { background: #fffbe6; border-color: #FFD500; }

/* Transitions */
.menu-drop-enter-active { transition: opacity 0.15s, transform 0.15s; }
.menu-drop-leave-active { transition: opacity 0.10s; }
.menu-drop-enter-from, .menu-drop-leave-to { opacity: 0; transform: translateY(-6px); }

.fade-slide-enter-active { transition: opacity 0.18s, transform 0.18s; }
.fade-slide-leave-active { transition: opacity 0.12s; }
.fade-slide-enter-from { opacity: 0; transform: translateY(-4px); }
.fade-slide-leave-to { opacity: 0; }

.slide-down-enter-active { transition: opacity 0.2s; }
.slide-down-leave-active { transition: opacity 0.15s; }
.slide-down-enter-from, .slide-down-leave-to { opacity: 0; }

.fade-enter-active, .fade-leave-active { transition: opacity 0.25s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

@keyframes spin { to { transform: rotate(360deg); } }
.spin { display: inline-block; animation: spin 1s linear infinite; }

button:focus-visible, a:focus-visible, input:focus-visible {
  outline: 2px solid #FFD500;
  outline-offset: 2px;
}
</style>
